import React, { useCallback, useEffect, useState } from "react";
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ShoppingCartIcon,
} from "@heroicons/react/24/outline";
import { apiService } from "../services/apiService";
import { WooCommerceConnectWizard } from "./WooCommerceConnectWizard";

interface WooCommerceSettingsProps {
  currentUser?: { role?: string };
  oauthState?: string | null;
  oauthStore?: string | null;
}

export const WooCommerceSettings: React.FC<WooCommerceSettingsProps> = ({
  currentUser,
  oauthState = null,
  oauthStore = null,
}) => {
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [connected, setConnected] = useState(false);
  const [isDemo, setIsDemo] = useState(false);
  const [isPlugin, setIsPlugin] = useState(false);
  const [storeUrl, setStoreUrl] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [inputUrl, setInputUrl] = useState("");
  const [consumerKey, setConsumerKey] = useState("");
  const [consumerSecret, setConsumerSecret] = useState("");
  const [autoInvoice, setAutoInvoice] = useState(true);

  const isAdmin = currentUser?.role === "admin" || currentUser?.role === "superadmin";

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await apiService.getWooCommerceSettings();
    const d = res.data as {
      enabled?: boolean;
      connected?: boolean;
      storeUrl?: string | null;
      isDemo?: boolean;
      isPlugin?: boolean;
      autoInvoice?: boolean;
    };
    if (res.error) setError(res.error);
    setEnabled(!!d?.enabled);
    setConnected(!!d?.connected);
    setIsDemo(!!d?.isDemo);
    setIsPlugin(!!d?.isPlugin);
    setStoreUrl(d?.storeUrl || "");
    setAutoInvoice(d?.autoInvoice !== false);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggleAutoInvoice = async (next: boolean) => {
    if (!isAdmin) return;
    setBusy(true);
    const res = await apiService.patchWooCommerceSettings(enabled, next);
    setBusy(false);
    if (res.error) setError(res.error);
    else setAutoInvoice(next);
  };

  const toggleEnabled = async (next: boolean) => {
    if (!isAdmin) return;
    setBusy(true);
    const res = await apiService.patchWooCommerceSettings(next, autoInvoice);
    setBusy(false);
    if (res.error) setError(res.error);
    else {
      setEnabled(next);
      setMessage(next ? "WooCommerce activado" : "WooCommerce desactivado");
    }
  };

  const handleDemo = async () => {
    if (!isAdmin) return;
    setBusy(true);
    const res = await apiService.connectWooCommerce({ useDemo: true });
    setBusy(false);
    if (res.error) setError(res.error);
    else {
      setMessage("Modo demo — pedido WC-DEMO-001");
      await load();
    }
  };

  const handleDisconnect = async () => {
    if (!isAdmin) return;
    setBusy(true);
    const res = await apiService.disconnectWooCommerce();
    setBusy(false);
    if (res.error) setError(res.error);
    else {
      setMessage("Desconectado");
      await load();
    }
  };

  const handleManualConnect = async () => {
    if (!isAdmin) return;
    setBusy(true);
    setError(null);
    const res = await apiService.connectWooCommerce({
      storeUrl: inputUrl.trim(),
      consumerKey: consumerKey.trim(),
      consumerSecret: consumerSecret.trim(),
    });
    setBusy(false);
    if (res.error) setError(res.message || res.error);
    else {
      setMessage("Conectado (modo avanzado)");
      setConsumerSecret("");
      await load();
    }
  };

  if (loading) {
    return <p className="text-sm text-zinc-500">Cargando WooCommerce…</p>;
  }

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-zinc-200/80 bg-white p-6 shadow-sm">
        <div className="flex items-start gap-3 mb-4">
          <div className="rounded-xl bg-violet-50 p-2">
            <ShoppingCartIcon className="h-6 w-6 text-violet-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-zinc-900">Tienda online · WooCommerce</h3>
            <p className="text-sm text-zinc-500 mt-0.5">
              Conexión recomendada: plugin Entikmedia (sin claves manuales).
            </p>
          </div>
        </div>

        {error && (
          <div className="flex gap-2 text-sm text-red-700 bg-red-50 rounded-xl px-3 py-2 mb-3">
            <ExclamationCircleIcon className="h-5 w-5 shrink-0" />
            {error}
          </div>
        )}
        {message && (
          <div className="flex gap-2 text-sm text-emerald-700 bg-emerald-50 rounded-xl px-3 py-2 mb-3">
            <CheckCircleIcon className="h-5 w-5 shrink-0" />
            {message}
          </div>
        )}

        <label className="flex items-center gap-3 cursor-pointer mb-4">
          <input
            type="checkbox"
            checked={enabled}
            disabled={!isAdmin || busy}
            onChange={(e) => toggleEnabled(e.target.checked)}
            className="rounded border-zinc-300 text-violet-600"
          />
          <span className="text-sm font-medium text-zinc-800">Activar WooCommerce</span>
        </label>

        {enabled && connected && (
          <div className="text-sm text-zinc-600 mb-4 space-y-2">
            <p>
              <span className="font-medium">Estado:</span> Conectado
              {isDemo ? " (demo)" : isPlugin ? " (plugin OAuth)" : ""}
            </p>
            {storeUrl && (
              <p>
                <span className="font-medium">Tienda:</span> {storeUrl}
              </p>
            )}
            {isAdmin && !isDemo && (
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoInvoice}
                  disabled={busy}
                  onChange={(e) => toggleAutoInvoice(e.target.checked)}
                  className="rounded border-zinc-300 text-violet-600"
                />
                <span>Factura automática al completar pedido (webhook)</span>
              </label>
            )}
            {isAdmin && (
              <button type="button" onClick={handleDisconnect} className="text-red-600 text-sm font-medium hover:underline block">
                Desconectar
              </button>
            )}
          </div>
        )}

        {enabled && !connected && isAdmin && (
          <>
            <WooCommerceConnectWizard oauthState={oauthState} initialStoreUrl={oauthStore || ""} onConnected={load} />
            <div className="mt-4 flex flex-wrap gap-2">
              <button type="button" onClick={handleDemo} disabled={busy} className="px-3 py-1.5 rounded-lg bg-zinc-100 text-sm">
                Modo demo
              </button>
              <button type="button" onClick={() => setShowAdvanced((v) => !v)} className="text-sm text-zinc-500">
                {showAdvanced ? "Ocultar avanzado" : "Conexión avanzada (REST API)"}
              </button>
            </div>
          </>
        )}

        {showAdvanced && enabled && isAdmin && !connected && (
          <div className="mt-4 space-y-2 border-t pt-4">
            <input type="url" placeholder="https://mitienda.com" value={inputUrl} onChange={(e) => setInputUrl(e.target.value)} className="w-full border rounded-xl px-3 py-2 text-sm" />
            <input type="text" placeholder="Consumer Key" value={consumerKey} onChange={(e) => setConsumerKey(e.target.value)} className="w-full border rounded-xl px-3 py-2 text-sm font-mono" />
            <input type="password" placeholder="Consumer Secret" value={consumerSecret} onChange={(e) => setConsumerSecret(e.target.value)} className="w-full border rounded-xl px-3 py-2 text-sm font-mono" />
            <button type="button" onClick={handleManualConnect} disabled={busy} className="px-4 py-2 rounded-xl bg-zinc-800 text-white text-sm">
              Conectar con claves REST
            </button>
          </div>
        )}
      </div>
      <p className="text-center text-[11px] text-zinc-400">Powered by Entikmedia</p>
    </div>
  );
};

export default WooCommerceSettings;
